import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthState } from '@/types';
import { api } from '@/lib/api';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  startImpersonation: (resellerId: string, resellerName: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// TOKEN STORAGE: Currently uses localStorage for simplicity
//
// SECURITY NOTE: localStorage is accessible to any JavaScript on the page,
// making it vulnerable to XSS attacks. For enhanced security, consider migrating
// to HttpOnly cookies in the future.
//
// FUTURE MIGRATION TO HttpOnly COOKIES:
// 1. Backend changes needed:
//    - Update login endpoint to set HttpOnly, Secure, SameSite=Strict cookie
//    - Update /api/auth/me to read token from cookie instead of header
//    - Add CSRF protection (double-submit cookie or sync token pattern)
//    - Set cookie path to '/api' to limit exposure
//
// 2. Frontend changes needed:
//    - Remove localStorage token storage below
//    - Remove Authorization header logic in api.ts
//    - Add credentials: 'include' to all fetch requests
//    - Handle CSRF token in requests if implementing CSRF protection
//
// 3. Files to modify:
//    - backend/src/routes/auth.ts (login endpoint cookie setting)
//    - backend/src/middleware/auth.ts (token extraction from cookie)
//    - frontend/src/lib/api.ts (remove token header, add credentials)
//    - frontend/src/hooks/useAuth.tsx (remove localStorage logic)
//
// Current approach is acceptable for low-traffic internal portal with
// proper Content-Security-Policy headers preventing inline scripts.
// =============================================================================

const TOKEN_KEY = 'reseller_portal_token';
const ADMIN_TOKEN_BACKUP_KEY = 'reseller_portal_admin_token_backup';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: localStorage.getItem(TOKEN_KEY),
    user: null,
    isAuthenticated: false,
    isLoading: true,
    impersonation: null,
  });

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        api.setToken(token);
        try {
          const { user, impersonation } = await api.getMe();
          setState({
            token,
            user,
            isAuthenticated: true,
            isLoading: false,
            impersonation: impersonation || null,
          });
        } catch {
          // If token is invalid, also clear any backup
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY);
          api.setToken(null);
          setState({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            impersonation: null,
          });
        }
      } else {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const { token, user } = await api.login(email, password);
    localStorage.setItem(TOKEN_KEY, token);
    api.setToken(token);
    setState({
      token,
      user,
      isAuthenticated: true,
      isLoading: false,
      impersonation: null,
    });
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY);
    api.setToken(null);
    setState({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      impersonation: null,
    });
  };

  const startImpersonation = async (resellerId: string, _resellerName: string) => {
    // Store current admin token for later restoration
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) {
      localStorage.setItem(ADMIN_TOKEN_BACKUP_KEY, currentToken);
    }

    // Request impersonation token from backend
    const response = await api.startImpersonation(resellerId);

    // Switch to impersonation token
    localStorage.setItem(TOKEN_KEY, response.impersonationToken);
    api.setToken(response.impersonationToken);

    // Fetch user context with impersonation info
    const { user, impersonation } = await api.getMe();

    setState({
      token: response.impersonationToken,
      user,
      isAuthenticated: true,
      isLoading: false,
      impersonation: impersonation || null,
    });
  };

  const stopImpersonation = async () => {
    // Call stop endpoint to log the action
    if (state.impersonation?.resellerId) {
      try {
        await api.stopImpersonation(state.impersonation.resellerId);
      } catch (e) {
        // Log but continue - we still want to restore the admin session
        console.error('Error stopping impersonation:', e);
      }
    }

    // Restore admin token
    const adminToken = localStorage.getItem(ADMIN_TOKEN_BACKUP_KEY);
    if (adminToken) {
      localStorage.setItem(TOKEN_KEY, adminToken);
      localStorage.removeItem(ADMIN_TOKEN_BACKUP_KEY);
      api.setToken(adminToken);

      // Fetch admin user context
      try {
        const { user } = await api.getMe();
        setState({
          token: adminToken,
          user,
          isAuthenticated: true,
          isLoading: false,
          impersonation: null,
        });
      } catch {
        // If admin token is now invalid, logout completely
        logout();
      }
    } else {
      // No backup token, logout
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
